import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ChevronLeft, ChevronRight, Flame, Tag, X, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ItemCard } from "../components/customer/ItemCard";
import { AddItemModal } from "../components/customer/AddItemModal";
import { CartBar } from "../components/customer/CartBar";
import { RestaurantLogo } from "../components/RestaurantLogo";
import { PoweredBy } from "../components/Brand";

export default function FoodCourtHome() {
  const navigate = useNavigate();
  const { table } = useCart();
  const [home, setHome] = useState(null);
  const [menu, setMenu] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [category, setCategory] = useState("All");
  const [modalItem, setModalItem] = useState(null);
  const [openRest, setOpenRest] = useState(null);
  const fcId = table?.foodcourt_id || "fc-chowk-2f";

  useEffect(() => {
    if (!table) { navigate("/"); return; }
    api.get(`/public/foodcourt/${fcId}/home`).then((r) => setHome(r.data));
    api.get(`/public/foodcourt/${fcId}/menu`).then((r) => setMenu(r.data));
  }, [fcId]);

  useEffect(() => { if (openRest) window.scrollTo(0, 0); }, [openRest]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/public/foodcourt/${fcId}/search`, { params: { q } }).then((r) => setResults(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, fcId]);

  const availableMenu = useMemo(() => menu.filter((m) => m.available !== false), [menu]);

  const filteredItems = useMemo(() => {
    if (category === "All") return [];
    return availableMenu.filter((m) => m.category === category);
  }, [availableMenu, category]);

  const byRestaurant = useMemo(() => {
    const g = {};
    availableMenu.forEach((m) => {
      (g[m.restaurant_id] = g[m.restaurant_id] || { name: m.restaurant_name, cuisine: m.group_name, items: [] }).items.push(m);
    });
    return g;
  }, [availableMenu]);

  if (!home) return <LoadingSkeleton />;

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#08183A] pb-28 relative">
      <div className="sticky top-0 z-50 glass px-4 py-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/order")} className="h-8 w-8 rounded-full card-surface-2 flex items-center justify-center"
                  data-testid="home-back-btn"><ChevronLeft size={18} /></button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-xs text-amber-400">
              <MapPin size={12} /> <span className="truncate font-medium">{table?.mall_name} · {table?.foodcourt_name}</span>
            </div>
            <div className="text-[11px] text-slate-300">Table <span className="text-slate-50 font-bold">{table?.table_code}</span> · {table?.zone}</div>
          </div>
        </div>
        <div className="mt-3 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes, restaurants or cuisines"
            className="w-full bg-[#16336B] rounded-xl pl-9 pr-9 py-2.5 text-sm text-slate-50 placeholder-slate-400 border hairline focus:border-amber-500 outline-none"
            data-testid="search-input" />
          {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" data-testid="clear-search"><X size={16} /></button>}
        </div>
        {/* categories — always visible so tabs can be switched from any view */}
        {results === null && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar pt-3">
            {["All", ...home.categories].map((c) => (
              <button key={c} onClick={() => { setCategory(c); setOpenRest(null); }}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${category === c ? "gold-gradient text-slate-950" : "card-surface-2 text-slate-300"}`}
                data-testid={`category-${c}`}>{c}</button>
            ))}
          </div>
        )}
      </div>

      {/* Food Buddy promo */}
      <button onClick={() => navigate("/order")} data-testid="open-buddy-from-menu"
        className="mx-4 mt-4 w-[calc(100%-2rem)] flex items-center gap-3 rounded-2xl p-3.5 gold-gradient text-slate-950 text-left">
        <Sparkles size={20} />
        <div className="leading-tight">
          <div className="font-display font-bold text-sm">Try Food Buddy</div>
          <div className="text-[11px] opacity-80">Just say what you want — I'll order it for you</div>
        </div>
      </button>

      {results !== null ? (
        <div className="p-4">
          <h3 className="font-display font-bold text-lg mb-3">Results for “{q}” <span className="text-slate-400 text-sm">({results.length})</span></h3>
          {results.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-10">No dishes found across the food court.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="search-results">
              {results.map((it) => <ItemCard key={it.id} item={it} onAdd={setModalItem} showRestaurant />)}
            </div>
          )}
        </div>
      ) : openRest ? (
        <div className="p-4">
          <button onClick={() => setOpenRest(null)} className="flex items-center gap-1 text-sm text-amber-400 mb-3" data-testid="rest-view-back"><ChevronLeft size={16} /> Back to all</button>
          <div className="flex items-center gap-2.5 mb-4">
            <RestaurantLogo name={byRestaurant[openRest]?.name || ""} size={44} />
            <div>
              <div className="font-display font-bold text-lg">{byRestaurant[openRest]?.name}</div>
              <div className="text-[11px] text-slate-400">{byRestaurant[openRest]?.cuisine} · {byRestaurant[openRest]?.items.length} items</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="restaurant-full-menu">
            {(byRestaurant[openRest]?.items || []).map((it) => <ItemCard key={it.id} item={it} onAdd={setModalItem} />)}
          </div>
        </div>
      ) : category !== "All" ? (
        <div className="p-4">
          <h3 className="font-display font-bold text-lg mb-3">{category} <span className="text-slate-400 text-sm">({filteredItems.length})</span></h3>
          {filteredItems.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-10">Nothing in this category right now.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="category-results">
              {filteredItems.map((it) => <ItemCard key={it.id} item={it} onAdd={setModalItem} showRestaurant />)}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="relative h-36 mx-4 mt-4 rounded-2xl overflow-hidden">
            <img src="https://images.pexels.com/photos/17428808/pexels-photo-17428808.jpeg?auto=compress&cs=tinysrgb&w=900"
                 alt="Food court" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08183A] via-transparent to-transparent" />
            <div className="absolute bottom-3 left-4">
              <div className="font-display font-extrabold text-xl">The whole food court, <span className="gold-text">one cart.</span></div>
              <div className="text-[11px] text-slate-200">{home.restaurants.length} restaurants live now</div>
            </div>
          </div>

          <Section title="Best Sellers" icon={Flame} items={home.sections.bestsellers} onAdd={setModalItem} />
          <Section title="Under ₹199" icon={Tag} items={home.sections.offers} onAdd={setModalItem} />

          <div className="px-4 mt-6">
            <h3 className="font-display font-bold text-lg mb-1">Restaurant Directory</h3>
            <p className="text-xs text-slate-400 mb-3">Order from any brand — it all lands in one master order.</p>
            {Object.entries(byRestaurant).map(([rid, r]) => (
              <div key={rid} className="mb-6" data-testid={`restaurant-section-${rid}`}>
                <button onClick={() => setOpenRest(rid)} className="w-full flex items-center justify-between mb-2 text-left" data-testid={`open-restaurant-${rid}`}>
                  <div className="flex items-center gap-2.5">
                    <RestaurantLogo name={r.name} size={38} />
                    <div>
                      <div className="font-semibold text-slate-100 flex items-center gap-1">{r.name} <ChevronRight size={14} className="text-amber-400" /></div>
                      <div className="text-[11px] text-slate-400">{r.cuisine} · View full menu</div>
                    </div>
                  </div>
                  <span className="text-[11px] text-emerald-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Open</span>
                </button>
                <div className="grid grid-cols-2 gap-3">
                  {r.items.map((it) => <ItemCard key={it.id} item={it} onAdd={setModalItem} />)}
                </div>
              </div>
            ))}
          </div>
          <PoweredBy />
        </>
      )}

      {modalItem && <AddItemModal item={modalItem} onClose={() => setModalItem(null)} />}
      <CartBar />
    </div>
  );
}

function Section({ title, icon: Icon, items, onAdd }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 px-4 mb-2">
        <Icon size={16} className="text-amber-400" />
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 pb-1">
        {items.map((it) => (
          <div key={it.id} className="w-40 shrink-0"><ItemCard item={it} onAdd={onAdd} showRestaurant /></div>
        ))}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#08183A] p-4 space-y-4">
      <div className="h-16 skeleton rounded-xl" />
      <div className="h-36 skeleton rounded-2xl" />
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-52 skeleton rounded-2xl" />)}
      </div>
    </div>
  );
}
